import { Injectable } from '@angular/core';

@Injectable()
export class HistoryHighlightService {

  private static readonly SCREEN_IDS: string[] = ['invoicereport'];

  private static readonly ITEM_KEYS: string[] = ['invoice_items', 'items'];

  private static readonly SKIP_ITEM_KEYS: string[] = [
    'sl_no', 'id', 'invoice_id', 'invoice_item_id', 'date_added', 'date_updated',
    'changed_fields', 'field_changes'
  ];

  private static readonly SKIP_DIV_KEYS: string[] = [
    'id', 'invoice_id', 'cs_id', 'company_id', 'user_id', 'date_updated',
    'changed_fields', 'field_changes', 'invoice_items', 'items', 'approvals',
    'attachments', 'meta_key'
  ];

  private static readonly DIV_KEY_ALIASES: any = {
    'invoice_no': ['vendor_invoice_no'],
    'date_added': ['invoice_date'],
    'assigned_user_id': ['assigned_user'],
    'vendor_id': ['vendor_address'],
    'vendor_location_id': ['vendor_address'],
    'pay_to_vendor_id': ['pay_to_party'],
    'pay_to_vendor_location_id': ['pay_to_party'],
    'pay_to_party_id': ['pay_to_party'],
    'channel_type_id': ['channel_type'],
    'voucher_type_id': ['voucher_type_name'],
    'invoice_approval_matrix_id': ['invoice_approval_matrix_name'],
    'purchase_type_document_id': ['purchase_type_document_name'],
    'purchase_organization_id': ['purchase_organization_name'],
    'purchase_group_id': ['purchase_group_name'],
    'transportation': ['transportaion'],
    'remark': ['remarks']
  };

  private static readonly LABEL_SUFFIXES: string[] = [
    '_name', '_nick_name', '_detail', '_details', '_no', ''
  ];

  private static readonly CODED_DIV_KEYS: string[] = ['channel_type'];

  markDivList(divList: any[], snapshot: any, screenId: string): void {
    if (!Array.isArray(divList)) {
      return;
    }
    for (const row of divList) {
      if (row) {
        row.is_changed = false;
        row.old_value = '';
      }
    }
    const changes = this.changesOf(snapshot, screenId);
    if (!changes) {
      return;
    }
    for (const key of this.changedDivKeys(snapshot, changes)) {
      const change = changes[key];
      const oldValue = change.old;
      if (this.blank(oldValue) || this.toArray(oldValue)) {
        continue;
      }
      const newValue = this.has(change, 'new') ? change.new : (snapshot ? snapshot[key] : undefined);
      if (this.same(oldValue, newValue)) {
        continue;
      }
      const row = this.divRowFor(divList, key, newValue, snapshot);
      if (!row) {
        continue;
      }
      row.is_changed = true;
      row.old_value = this.oldDivText(row, key, oldValue);
    }
  }

  markItems(items: any[], columnList: any[], snapshot: any, tableId: string): void {
    if (!Array.isArray(items)) {
      return;
    }
    for (const item of items) {
      if (item && typeof item === 'object') {
        item.changed_cells = null;
      }
    }
    const changes = this.changesOf(snapshot, tableId);
    if (!changes || !Array.isArray(columnList) || columnList.length === 0) {
      return;
    }

    let oldItems: any[] = null;
    for (const key of HistoryHighlightService.ITEM_KEYS) {
      if (changes[key]) {
        oldItems = this.toArray(changes[key].old);
        if (oldItems) {
          break;
        }
      }
    }
    if (!oldItems || oldItems.length === 0) {
      return;
    }

    const byProduct = this.indexByProduct(oldItems);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item || typeof item !== 'object') {
        continue;
      }
      const old = byProduct ? byProduct[this.text(item.product_id)] : oldItems[i];
      if (!old) {
        continue;
      }
      const cells: any = {};
      for (const column of columnList) {
        const key = column && column.value ? column.value : '';
        if (!key || HistoryHighlightService.SKIP_ITEM_KEYS.indexOf(key) > -1) {
          continue;
        }
        const oldValue = this.itemValue(old, key);
        if (oldValue === undefined && !this.has(item, key)) {
          continue;
        }
        if (this.blank(oldValue) || this.same(oldValue, item[key]) || this.composed(oldValue, item[key])) {
          continue;
        }
        cells[key] = this.text(oldValue);
      }
      if (Object.keys(cells).length > 0) {
        item.changed_cells = cells;
      }
    }
  }

  private changesOf(snapshot: any, screenId: string): any {
    if (HistoryHighlightService.SCREEN_IDS.indexOf(screenId) === -1) {
      return null;
    }
    if (!snapshot || !Array.isArray(snapshot.changed_fields) || !snapshot.field_changes) {
      return null;
    }
    const changes = snapshot.field_changes;
    for (const key of snapshot.changed_fields) {
      if (changes[key] && !this.blank(changes[key].old)) {
        return changes;
      }
    }
    return null;
  }

  private changedDivKeys(snapshot: any, changes: any): string[] {
    const keys: string[] = [];
    for (const key of snapshot.changed_fields) {
      if (typeof key !== 'string' || HistoryHighlightService.SKIP_DIV_KEYS.indexOf(key) > -1) {
        continue;
      }
      if (changes[key] && keys.indexOf(key) === -1) {
        keys.push(key);
      }
    }
    return keys;
  }

  private divRowFor(divList: any[], key: string, newValue: any, snapshot: any): any {
    const aliases = HistoryHighlightService.DIV_KEY_ALIASES[key];
    const names: string[] = [key].concat(Array.isArray(aliases) ? aliases : []);
    for (const row of divList) {
      if (!row || row.is_changed) {
        continue;
      }
      if (this.matchesName(row.id, names) || this.matchesName(row.custom_key, names)) {
        return row;
      }
    }
    const byValue = this.divRowByValue(divList, newValue);
    if (byValue) {
      return byValue;
    }
    for (const label of this.labelValues(snapshot, key)) {
      const row = this.divRowByValue(divList, label);
      if (row) {
        return row;
      }
    }
    return null;
  }

  private labelValues(snapshot: any, key: string): any[] {
    if (!snapshot) {
      return [];
    }
    const base = key.lastIndexOf('_id') === key.length - 3 ? key.substring(0, key.length - 3) : key;
    if (base === '') {
      return [];
    }
    const values: any[] = [];
    for (const suffix of HistoryHighlightService.LABEL_SUFFIXES) {
      const name = base + suffix;
      if (name === key || !this.has(snapshot, name)) {
        continue;
      }
      if (!this.blank(snapshot[name])) {
        values.push(snapshot[name]);
      }
    }
    return values;
  }

  private matchesName(name: any, names: string[]): boolean {
    return typeof name === 'string' && name !== '' && names.indexOf(name) > -1;
  }

  private divRowByValue(divList: any[], newValue: any): any {
    const target = this.text(newValue);
    if (target.length < 2 || this.isFlag(target)) {
      return null;
    }
    let found: any = null;
    for (const row of divList) {
      if (!row || row.is_changed || row.input_type === 'checkbox') {
        continue;
      }
      if (this.text(row.value) !== target) {
        continue;
      }
      if (found) {
        return null;
      }
      found = row;
    }
    return found;
  }

  private oldDivText(row: any, key: string, oldValue: any): string {
    if (row && row.input_type === 'checkbox') {
      return this.isFlagOn(oldValue) ? 'Yes' : 'No';
    }
    if (this.isBareId(key, oldValue)) {
      return '';
    }
    if (HistoryHighlightService.CODED_DIV_KEYS.indexOf(key) > -1 && this.num(oldValue) !== null) {
      return '';
    }
    if (this.isFlagBehindLabel(row, oldValue)) {
      return '';
    }
    return this.text(oldValue);
  }

  private isFlagBehindLabel(row: any, oldValue: any): boolean {
    if (!this.isFlag(this.text(oldValue))) {
      return false;
    }
    const shown = this.text(row ? row.value : '');
    return shown !== '' && this.num(shown) === null;
  }

  private isFlag(text: string): boolean {
    return text === '0' || text === '1';
  }

  private isFlagOn(value: any): boolean {
    const text = this.text(value).toLowerCase();
    return text === '1' || text === 'true' || text === 'yes';
  }

  private indexByProduct(oldItems: any[]): any {
    const index: any = {};
    const seen: string[] = [];
    for (const old of oldItems) {
      const id = this.text(old && old.product_id);
      if (id === '' || id === '0' || seen.indexOf(id) > -1) {
        return null;
      }
      seen.push(id);
      index[id] = old;
    }
    return index;
  }

  private itemValue(old: any, key: string): any {
    if (this.has(old, key)) {
      return old[key];
    }
    if (old && old.product && this.has(old.product, key)) {
      return old.product[key];
    }
    return undefined;
  }

  private has(source: any, key: string): boolean {
    return !!source && Object.prototype.hasOwnProperty.call(source, key);
  }

  private toArray(value: any): any[] {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim().charAt(0) === '[') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : null;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  private blank(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    const text = String(value).trim();
    return text === '' || text === '[]' || text === '{}' || text === 'null' || text === 'undefined';
  }

  private text(value: any): string {
    if (value === null || value === undefined || typeof value === 'object') {
      return '';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value).trim();
  }

  private num(value: any): number {
    const text = this.text(value).replace(/,/g, '');
    if (text === '' || typeof value === 'boolean') {
      return null;
    }
    const parsed = Number(text);
    return isFinite(parsed) ? parsed : null;
  }

  private same(a: any, b: any): boolean {
    const left = this.num(a);
    const right = this.num(b);
    if (left !== null && right !== null) {
      return left === right;
    }
    return this.text(a).toLowerCase() === this.text(b).toLowerCase();
  }

  private composed(oldValue: any, newValue: any): boolean {
    if (this.num(oldValue) !== null && this.num(newValue) !== null) {
      return false;
    }
    const old = this.text(oldValue).toLowerCase();
    const next = this.text(newValue).toLowerCase();
    return old.length > 1 && next.length > old.length && next.indexOf(old) > -1;
  }

  private isBareId(key: string, oldValue: any): boolean {
    return key.lastIndexOf('_id') === key.length - 3 && this.num(oldValue) !== null;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src825240186/src/src/app/shared/services/history-highlight.service.ts